import { lazy } from "react";

export interface RouteConfig {
  path: string;
  name: string;
  exact: boolean;
  component: React.ComponentType<any>;
  /** false hides the marketing header, footer and scroll-to-top. */
  chrome?: boolean;
}

/**
 * Route table.
 *
 * Pages are code-split, so a visitor landing on /terms_of_service doesn't
 * download the home page's gallery and modal code, and vice versa.
 *
 * `chrome: false` hides the marketing header and footer for that route.
 *
 * ---------------------------------------------------------------------------
 * PATHS ARE LOAD-BEARING. Do not "tidy" these into shorter names.
 *
 * The paths below are the ones live on theghostgame.com today, checked against
 * production rather than assumed. Three of them are referenced from places we
 * don't control:
 *
 *   /privacy_policy    — the URL both store listings give as the privacy policy.
 *                        Changing it breaks App Store and Play Console review.
 *   /terms_of_service  — linked from the store listings and the game client.
 *   /rules_of_conduct  — the document the in-game sanction messages point at.
 *
 * Shorter aliases (/terms, /privacy) redirect to these, not the other way
 * round, so the canonical URL stays the one already in the wild. See
 * router/redirects.ts.
 * ---------------------------------------------------------------------------
 */
const routes: RouteConfig[] = [
  {
    path: "/",
    name: "Home",
    exact: true,
    component: lazy(() => import("../pages/Home")),
  },
  {
    path: "/about",
    name: "About Us",
    exact: true,
    component: lazy(() => import("../pages/About")),
  },
  {
    path: "/terms_of_service",
    name: "Terms of Service",
    exact: true,
    component: lazy(() => import("../pages/Terms")),
  },
  {
    path: "/privacy_policy",
    name: "Privacy Policy",
    exact: true,
    component: lazy(() => import("../pages/Privacy")),
  },
  {
    path: "/rules_of_conduct",
    name: "Rules of Conduct",
    exact: true,
    component: lazy(() => import("../pages/Conduct")),
  },
  {
    /* Unlisted on purpose: not in content/nav.ts, so it appears in no menu.
       Reachable only by typing the URL, and noindex'd. That is obscurity, not
       access control — see the security note in pages/Admin.

       There is deliberately NO redirect from the old /admin (see
       router/redirects.ts): a redirect would hand the new path to anyone who
       tried the old one, which is the one thing an unlisted URL has to avoid.
       /admin now 404s like any other unknown path. */
    path: "/admin-2nu7h",
    name: "Admin",
    exact: true,
    component: lazy(() => import("../pages/Admin")),
    chrome: false,
  },
  {
    /* Same treatment as the console above: unlisted, noindex, no redirect from
       a guessable /transfer. See pages/Transfer for what the exec key is
       worth here. */
    path: "/transfer-949dd8",
    name: "Account transfer",
    exact: true,
    component: lazy(() => import("../pages/Transfer")),
    chrome: false,
  },
];

export default routes;
